// ─────────────────────────────────────────────────────────────────────────────
// BehavioralCostHero
//
// Page-level hero card for the Analytics tab. Sums estimated monthly cost
// across all four detector suites (Behavior, Risk, Psychology, Context) and
// shows it as a single big number above the tab navigation.
//
// Why this exists:
//   The combined cost lives inside each suite — Behavior shows its number on
//   the Behavior tab, Risk on the Risk tab, etc. A new user clicking around
//   never sees the grand total. That grand total is the single most
//   conversion-critical number in the product. This hero surfaces it.
//
// Data:
//   Takes the `allDetectorResults` object returned by useDetectors({suite:"all"})
//   in the parent. Shape:
//     { behavior: {detectors, total_monthly_cost},
//       risk:     {detectors, total_monthly_cost},
//       psychology: {detectors, total_monthly_cost},
//       context:  {detectors, total_monthly_cost} }
//
//   Each suite's total_monthly_cost is the suite-level number already used by
//   the existing in-suite headlines, so the math here matches what users see
//   when they drill in (no surprise "where did this number come from?").
//
// States:
//   loading       → subtle placeholder
//   no trades     → not rendered (parent handles)
//   no patterns   → green "no costly patterns" card
//   has patterns  → the big red hero number
//   error         → silent — don't block the rest of the page
//
// IMPORTANT: this hero does NOT subtract acknowledged/dismissed patterns.
// Doing that properly requires loading per-detector state from Supabase,
// which adds latency and complexity. The per-suite numbers already do this
// inside their cards, so users who care can drill in. The hero is the
// "uncurated reality" — what your trades say without your filters applied.
// ─────────────────────────────────────────────────────────────────────────────

import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";

const SUITE_LABELS = {
  behavior:   "Behavior",
  risk:       "Risk",
  psychology: "Psychology",
  context:    "Context",
};

export function BehavioralCostHero({ allDetectorResults, loading, tradeCount }) {
  // Don't render at all if there's no data to analyze.
  // Parent should not render us before trades exist, but defensive.
  if (!tradeCount || tradeCount === 0) return null;

  // Loading state: low-contrast placeholder so the page doesn't shift when
  // data arrives.
  if (loading) {
    return (
      <div style={{
        marginBottom: 16,
        padding: "16px 20px",
        background: "#1a1d2e",
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        minHeight: 96,
        display: "flex",
        alignItems: "center",
        color: C.muted,
        fontSize: 13,
      }}>
        <span style={{ marginRight: 10 }}>⚙️</span> Scanning behavioral patterns…
      </div>
    );
  }

  // Edge function returned nothing (subscription gate, error, etc).
  // Silent — don't block the page.
  if (!allDetectorResults) return null;

  // Sum costs across all four suites.
  // Each suite returns total_monthly_cost as an already-aggregated number;
  // we trust that math (it's the same math each suite uses for its own headline).
  const suiteContributions = [];
  let totalCost = 0;

  for (const suiteKey of ["behavior", "risk", "psychology", "context"]) {
    const suite = allDetectorResults[suiteKey];
    const cost = suite?.total_monthly_cost ?? 0;
    if (cost > 0) {
      suiteContributions.push({ key: suiteKey, label: SUITE_LABELS[suiteKey], cost });
      totalCost += cost;
    }
  }

  // Sort contributions by cost descending so the biggest leak reads first.
  suiteContributions.sort((a, b) => b.cost - a.cost);

  // Healthy state: nothing costly detected.
  if (totalCost === 0) {
    return (
      <Card style={{
        marginBottom: 16,
        background: C.green + "0a",
        border: `1px solid ${C.green}44`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: "50%",
            background: C.green + "1f",
            border: `2px solid ${C.green}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, color: C.green, fontWeight: 700, flexShrink: 0,
          }}>✓</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>
              No costly behavioral patterns detected
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              Across {tradeCount} trades, none of the 22 detectors flagged a meaningful drain on your account. Keep at it.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // The hero state: real cost detected. This is the conversion moment.
  return (
    <Card style={{
      marginBottom: 16,
      background: C.red + "0a",
      border: `1px solid ${C.red}55`,
      borderLeft: `4px solid ${C.red}`,
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 16,
        flexWrap: "wrap",
      }}>
        {/* Left: label + descriptor */}
        <div style={{ flex: "1 1 280px", minWidth: 240 }}>
          <div style={{
            fontSize: 10,
            color: C.red,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontWeight: 700,
            marginBottom: 4,
          }}>
            Estimated monthly cost of behavioral patterns
          </div>
          <div style={{
            fontSize: 13,
            color: C.text,
            lineHeight: 1.5,
            marginBottom: 8,
          }}>
            Combined drain from patterns flagged across all 4 detector suites — observed in your trades, extrapolated to a monthly figure.
          </div>

          {/* Per-suite contributions, biggest first */}
          {suiteContributions.length > 0 && (
            <div style={{
              fontSize: 11,
              color: C.muted,
              fontFamily: "monospace",
              marginTop: 8,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}>
              {suiteContributions.map((s, i) => (
                <span key={s.key}>
                  <span style={{ color: C.sub }}>{s.label}</span>
                  {" "}
                  <span style={{ color: C.red, fontWeight: 700 }}>{fmt$(-s.cost)}</span>
                  {i < suiteContributions.length - 1 && (
                    <span style={{ color: C.border, marginLeft: 12 }}>·</span>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right: the big number */}
        <div style={{
          textAlign: "right",
          flexShrink: 0,
          alignSelf: "center",
        }}>
          <div style={{
            fontSize: 36,
            fontWeight: 900,
            color: C.red,
            fontFamily: "monospace",
            lineHeight: 1.1,
          }}>
            {fmt$(-totalCost)}
          </div>
          <div style={{
            fontSize: 11,
            color: C.muted,
            marginTop: 2,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}>
            per month
          </div>
        </div>
      </div>

      {/* Caveat strip — keeps the framing honest */}
      <div style={{
        marginTop: 14,
        paddingTop: 12,
        borderTop: `1px solid ${C.red}22`,
        fontSize: 11,
        color: C.muted,
        lineHeight: 1.5,
      }}>
        Estimates based on the gap between your pattern-affected trades and your baseline performance. Drill into a suite tab below to see which specific patterns drive the most cost.
      </div>
    </Card>
  );
}
